import React from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import type { IInPersonCandidate } from './utils';

// Every side's colour is named individually, and never the border-color
// shorthand: the config sets important, so a shorthand would beat the inline
// accent below and flatten every row to grey. Preflight is off, so a side left
// unnamed falls back to currentColor rather than a sensible default.
const rowBase =
  'flex w-full items-center p-4 mb-4 last:mb-0 border border-l-[6px] border-y-grey-400 border-r-grey-400 bg-transparent [font:inherit] text-left';
// Built from the base rather than appended to each other. Tailwind keeps
// whichever utility sits later in the stylesheet, not later in the string, so
// cursor-default after cursor-pointer does not win.
const row = `${rowBase} cursor-pointer hover:bg-grey-100 disabled:cursor-default disabled:opacity-60`;
const waitingRow = `${rowBase} border-l-grey-400 text-grey-700 cursor-default`;
const nameCell = 'flex-[0_0_40%] pr-4 font-bold';
const emailCell = 'grow text-grey-700 [overflow-wrap:anywhere]';
const message = 'p-4 text-center text-grey-700';

interface IProps {
  signers: IInPersonCandidate[];
  isLoading?: boolean;
  error?: string | null;
  startingSignerId?: string | null;
  onSelect: (signer: IInPersonCandidate) => void;
  onClose: () => void;
}

export function WhosSigningModal({ signers, isLoading, error, startingSignerId, onSelect, onClose }: IProps) {
  const isStarting = Boolean(startingSignerId);
  const eligible = signers.filter((s) => s.isEligible && !s.isSender);
  const waiting = signers.filter((s) => !s.isEligible && !s.isSender);

  return (
    <Dialog
      open
      onClose={isStarting ? undefined : onClose}
      classes={{ paper: 'max-w-[620px] w-full p-8 pt-10' }}
      aria-labelledby="whos-signing-title"
      data-spec="whos-signing-modal"
      // The row underneath opens the PDF preview on click.
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-10">
        <Typography id="whos-signing-title" className="text-3xl font-bold font-serif" component="h2">
          Who&apos;s signing?
        </Typography>
        <IconButton
          className="text-grey-500 absolute top-2 right-2"
          onClick={onClose}
          disabled={isStarting}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {error ? (
        <Typography className="mb-4 text-red-500" role="alert" data-spec="whos-signing-error">
          {error}
        </Typography>
      ) : null}

      {isLoading ? (
        <div className={message}>
          <CircularProgress size={28} />
        </div>
      ) : eligible.length === 0 && waiting.length === 0 ? (
        <Typography className={message} data-spec="whos-signing-empty">
          Nobody on this envelope is waiting to sign right now.
        </Typography>
      ) : (
        <>
          {eligible.map((signer) => (
            <button
              type="button"
              key={signer.signerId}
              className={row}
              style={{ borderLeftColor: signer.color }}
              onClick={() => onSelect(signer)}
              disabled={isStarting}
              data-spec={`whos-signing-option-${signer.signerId}`}
            >
              {/* span because a button may only contain phrasing content. */}
              <Typography className={nameCell} component="span">
                {signer.name}
              </Typography>
              <Typography className={emailCell} component="span">
                {signer.email}
              </Typography>
              <IconButton
                className="ml-4 shrink-0 bg-blue-100 text-blue-800"
                component="span"
                disableRipple
                aria-hidden
              >
                {startingSignerId === signer.signerId ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
              </IconButton>
            </button>
          ))}

          {waiting.length > 0 && (
            <>
              <Typography
                className="mt-6 mb-2 text-[12px] uppercase tracking-[0.08em] text-grey-700"
                data-spec="whos-signing-waiting-label"
              >
                Waiting on active signing group
              </Typography>
              {waiting.map((signer) => (
                <div key={signer.signerId} className={waitingRow} data-spec={`whos-signing-waiting-${signer.signerId}`}>
                  <Typography className={nameCell}>{signer.name}</Typography>
                  <Typography className={emailCell}>{signer.email}</Typography>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </Dialog>
  );
}
